import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { IngredientListScreen } from '@/features/ingredients/IngredientListScreen';
import { parseIngredientListSearch, type IngredientListSearch } from '@/features/ingredients/lib/sort';

/**
 * /ingredients, with the list's state in the URL so every view can be linked
 * to and survives a reload:
 *   ?filter=needs_price | unused   the segment (the dashboard's Estimates
 *                                  link opens ?filter=needs_price)
 *   ?group=<id> | none             the group select
 *   ?search=...                    the filter box (the palette's "See all",
 *                                  the form's name-collision "View existing")
 * The input is read leniently (needs-price, a number or a string id), so a
 * link written either way lands; the defaults never appear in the URL.
 *
 * A change rebuilds the query from the three keys the list reads, so a key
 * that only opened the view (the legacy ?unused_only=1) is dropped the moment
 * the chef picks another segment, instead of reopening Unused on a reload.
 */
export const Route = createFileRoute('/_app/ingredients/')({
  validateSearch: (
    s: { search?: string; filter?: string; group?: number | string } & SearchSchemaInput,
  ): IngredientListSearch => parseIngredientListSearch(s),
  component: IngredientsRoute,
});

function IngredientsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <IngredientListScreen
      search={search}
      onSearchChange={(patch) =>
        void navigate({ search: (prev) => ({ ...parseIngredientListSearch(prev), ...patch }), replace: true })
      }
    />
  );
}
